import { clip } from './engine';
import { bboxOverlaps, boundingBox, multiPolygonArea, pcRingArea, toPcRing } from './geometry';
import { classifyCutter } from './subtract';
import type { Ring, SubtractBase } from './types';

/** Validating where a candidate opening sits relative to a layer's rings. */

export type CutoutPlacementReason =
	| 'not-closed'
	| 'degenerate'
	| 'outside-areas'
	| 'overlaps-cutout';

export type CutoutPlacementResult = { ok: true } | { ok: false; reason: CutoutPlacementReason };

/**
 * Overlap smaller than this fraction of the candidate's own area is treated as
 * numeric noise from clipping rather than a real intersection.
 */
const relativeTolerance = 1e-6;

/**
 * Classify whether `ring` may be persisted as a cut-out of `base`.
 *
 * The stored invariants (see the `cutouts` JSDoc on the Path layer type) demand a
 * cut-out lie inside exactly ONE outer ring and overlap no sibling. The toggle
 * flows tolerate misplaced rings because they move geometry the user already has;
 * this check exists for flows that CREATE a ring as a cut-out, where refusing a
 * bad placement loses nothing but a redraw.
 *
 * A clipping-engine failure refuses containment (nothing proves the ring sits
 * inside an area) but passes the sibling-overlap probe, mirroring how
 * `clipHolesToRing` degrades: a broken engine never invents an overlap.
 *
 * @param base The layer's current rings.
 * @param ring The candidate opening.
 * @returns Whether the placement holds, and the first violated rule when not.
 */
export const classifyCutoutPlacement = (base: SubtractBase, ring: Ring): CutoutPlacementResult => {
	const shapeReason = classifyCutter(ring);
	if (shapeReason === 'not-closed' || shapeReason === 'degenerate') {
		return { ok: false, reason: shapeReason };
	}

	const ringArea = pcRingArea(toPcRing(ring));
	if (ringArea <= 0) return { ok: false, reason: 'degenerate' };

	const ringBox = boundingBox(ring);
	const noise = ringArea * relativeTolerance;

	// Inside exactly one outer ring: full containment (intersection recovers the
	// ring's whole area) in one, no real overlap with any other. A partial overlap
	// anywhere means the ring crosses a boundary and is refused outright.
	let containedBy = 0;

	for (const outer of base.paths) {
		if (classifyCutter(outer)) continue;
		if (!bboxOverlaps(boundingBox(outer), ringBox)) continue;

		const overlap = clip.intersection([toPcRing(ring)], [toPcRing(outer)]);
		const overlapArea = overlap === null ? 0 : multiPolygonArea(overlap);

		if (overlapArea <= noise) continue;
		if (overlapArea < ringArea - noise) return { ok: false, reason: 'outside-areas' };

		containedBy += 1;
	}

	if (containedBy !== 1) return { ok: false, reason: 'outside-areas' };

	// No overlap with an existing opening. Full containment is also an overlap, so
	// this refuses nesting too.
	for (const cutout of base.cutouts ?? []) {
		if (classifyCutter(cutout)) continue;
		if (!bboxOverlaps(boundingBox(cutout), ringBox)) continue;

		const overlap = clip.intersection([toPcRing(ring)], [toPcRing(cutout)]);
		if (overlap !== null && multiPolygonArea(overlap) > noise) {
			return { ok: false, reason: 'overlaps-cutout' };
		}
	}

	return { ok: true };
};
